import { useMutation, useQueryClient } from '@tanstack/react-query';
import useApi, {
    SearchParams,
    useApiMutation,
    UseApiMutationResult,
    UseApiResult,
} from 'hooks/useApi';
import useDownload from 'hooks/useDownload';
import { t } from 'i18next';
import BaseService from 'services/api/BaseService';
import { DossiersUrls } from 'services/api/urls';
import {
    AdemeVulVehicle,
    CalculPrimeConcessionnaireParams,
    CalculPrimeParticulierConsessionnaireParams,
    ClientType,
} from 'types/Concessionnaire';
import {
    CreateDossierLead,
    CreateDossierNotePayload,
    DeleteDossierNoteResponse,
    DepartmentCodeResponse,
    DossierDetails,
    DossierNote,
    DossierNotes,
    Dossiers,
    DossiersSearchParams,
    Personne,
    UpdateDossierNotePayload,
} from 'types/Dossier';
import {
    OperationDetails,
    Operations,
    OperationsSearchParams,
} from 'types/Operation';
import { ViewMode } from '../components';
import { Company } from '../types/Company';

const NO_PRIME_CONCESSIONNAIRE_MESSAGE =
  'Aucune prime concessionnaire ne correspond aux critères fournis.';
const NO_PRIME_AVAILABLE_FORM_MESSAGE =
  'Aucune prime disponible pour les données saisies.';
const DOSSIER_NOTES_QUERY_KEY = 'dossier-notes';

export function getDossierNotesQueryKey(dossierId: string) {
  return [DOSSIER_NOTES_QUERY_KEY, dossierId];
}

async function requestDossierNoteJson<ReturnType, BodyType = undefined>(
  url: string,
  method: string,
  body?: BodyType,
): Promise<ReturnType> {
  const response = await BaseService.request(url, method, true, body, false);

  const isSuccess = [200, 201, 204].includes(response?.status);
  let data: any;

  if (response?.status !== 204 && typeof response?.json === 'function') {
    try {
      data = await response.json();
    } catch {
      data = undefined;
    }
  }

  if (isSuccess) {
    return data as ReturnType;
  }

  throw new Error(data?.message || t('global:internet_connexion_error'));
}

function getCalculPrimeConcessionnaireErrorMessage(body?: {
  message?: string;
  statusCode?: number;
}) {
  if (
    body?.statusCode === 400 &&
    body.message === NO_PRIME_CONCESSIONNAIRE_MESSAGE
  ) {
    return NO_PRIME_AVAILABLE_FORM_MESSAGE;
  }

  return body?.message || 'Erreur lors du calcul de la prime et du volume';
}

export function useCreateDossierMutation(): UseApiMutationResult<
  CreateDossierLead,
  { id: string }
> {
  return useApiMutation<CreateDossierLead, { id: string }>(DossiersUrls.ADD, {
    method: 'POST',
    isJson: false,
  });
}

export function useDossiers(
  searchParams: DossiersSearchParams,
  viewMode?: ViewMode,
): UseApiResult<Dossiers> {
  return useApi<Dossiers>(DossiersUrls.LIST, {
    searchParams: searchParams as SearchParams,
    key: [viewMode, searchParams],
  });
}

export function useReferentsCommerciaux(
  searchParams: {
    statuts?: string[];
  } = {},
): UseApiResult<Personne[]> {
  return useApi<Personne[]>(DossiersUrls.GET_REFERENTS_COMMERCIAUX, {
    searchParams: searchParams as SearchParams,
  });
}

export function useDossier(dossierId: string): UseApiResult<DossierDetails> {
  return useApi<DossierDetails>(DossiersUrls.GET(dossierId));
}

export function useOperations(
  dossierId: string,
  searchParams: OperationsSearchParams,
): UseApiResult<Operations> {
  return useApi<Operations>(DossiersUrls.OPERATIONS_WITH_PARAMS(dossierId), {
    searchParams: searchParams as SearchParams,
  });
}

export function useOperation(
  dossierId?: string,
  operationId?: string,
): UseApiResult<OperationDetails> {
  return useApi<OperationDetails>(
    dossierId && operationId
      ? DossiersUrls.OPERATION(dossierId, operationId)
      : null,
  );
}

export function useDossierNotes(dossierId: string): UseApiResult<DossierNotes> {
  return useApi<DossierNotes>(DossiersUrls.NOTES(dossierId), {
    key: getDossierNotesQueryKey(dossierId),
    staleTime: 0,
  });
}

export function useCreateDossierNote(dossierId: string) {
  const queryClient = useQueryClient();

  return useMutation<DossierNote, Error, CreateDossierNotePayload>({
    mutationFn: (body) =>
      requestDossierNoteJson<DossierNote, CreateDossierNotePayload>(
        DossiersUrls.NOTES(dossierId),
        'POST',
        body,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getDossierNotesQueryKey(dossierId),
      }),
  });
}

export function useUpdateDossierNote(dossierId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    DossierNote,
    Error,
    { noteId: string; body: UpdateDossierNotePayload }
  >({
    mutationFn: ({ noteId, body }) =>
      requestDossierNoteJson<DossierNote, UpdateDossierNotePayload>(
        DossiersUrls.NOTE(dossierId, noteId),
        'PUT',
        body,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getDossierNotesQueryKey(dossierId),
      }),
  });
}

export function useDeleteDossierNote(dossierId: string) {
  const queryClient = useQueryClient();

  return useMutation<DeleteDossierNoteResponse, Error, string>({
    mutationFn: (noteId) =>
      requestDossierNoteJson<DeleteDossierNoteResponse>(
        DossiersUrls.NOTE(dossierId, noteId),
        'DELETE',
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getDossierNotesQueryKey(dossierId),
      }),
  });
}

export const useDownloadDossiersCSV = (
  filters?: Pick<
    DossiersSearchParams,
    | 'keyword'
    | 'referentsCommerciauxEmails'
    | 'statuts'
    | 'companyId'
    | 'ficheDeLiaisonId'
    | 'dateFinChantierMin'
    | 'dateFinChantierMax'
    | 'accountId'
    | 'demandeursCee'
  >,
) => {
  return useDownload(DossiersUrls.EXPORT, {
    searchParams: filters,
  }).download;
};

export const useUploadDossierAttachmentAction = (dossierId: string): string => {
  return DossiersUrls.ATTACHMENTS(dossierId);
};

/**
 * Hook pour obtenir le code département à partir d'un code postal
 * @param postalCode - Code postal français à 5 chiffres (ex: "75001")
 * @returns UseApiResult contenant le code département ou une erreur si le code postal est invalide
 */
export function useDepartmentCodeByPostalCode(
  postalCode: string | null | undefined,
): UseApiResult<DepartmentCodeResponse> {
  return useApi<DepartmentCodeResponse>(
    postalCode && postalCode.length === 5
      ? DossiersUrls.GET_DEPARTMENT_CODE(postalCode)
      : null,
    { enabled: false, retry: false },
  );
}

export function useNouveauDossierConcessionnaireMutation() {
  return useMutation<
    { id: string; volume: number; prime: number },
    Error,
    { particulier: boolean; formData: FormData }
  >({
    mutationFn: async ({ particulier, formData }) => {
      let body;
      const response = await BaseService.request(
        particulier ? DossiersUrls.NEW_FORM_PARTICULIER : DossiersUrls.NEW_FORM,
        'POST',
        true,
        formData,
        true,
      );

      try {
        body = await response.json();

        if (!response.ok) {
          throw new Error(
            body?.message ?? t('nouveau_formulaire:errors:unknown'),
          );
        }
      } catch (error) {
        throw new Error(t('nouveau_formulaire:errors:unknown'));
      }

      return body;
    },
  });
}

export function useAdemeVulVehicles(): UseApiResult<AdemeVulVehicle[]> {
  return useApi<AdemeVulVehicle[]>(DossiersUrls.ADEME_VUL_VEHICLES, {
    key: ['ademe-vul-vehicles'],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useCalculPrimeConcessionnaire() {
  return useMutation<
    {
      primeParVehicule: number;
      primeParGwh: number;
      volume: number;
    },
    Error,
    | CalculPrimeConcessionnaireParams
    | CalculPrimeParticulierConsessionnaireParams
  >({
    mutationFn: async (body) => {
      const response = await BaseService.request(
        body.typeClientFinal === ClientType.PERSONNE_PHYSIQUE
          ? DossiersUrls.CALCULATE_PRIME_PARTICULIER
          : DossiersUrls.CALCULATE_PRIME,
        'POST',
        true,
        body,
        false,
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(
          () =>
            undefined as unknown as {
              message?: string;
              statusCode?: number;
            },
        );

        throw new Error(getCalculPrimeConcessionnaireErrorMessage(errorBody));
      }

      return await response.json();
    },
  });
}

/**
 * Hook pour obtenir les entreprises de travaux liées au compte connecté.
 * Retourne un tableau d'entreprises
 */
export function useCompanies(enabled: boolean): UseApiResult<Company[]> {
  return useApi<Company[]>(DossiersUrls.GET_COMPANIES, {
    staleTime: 5 * 1000,
    retry: false,
    enabled,
  });
}
